import type { AxiosRequestConfig } from "axios";

import { api } from "@/services/api";
import { APIError, FormError } from "@/utils";

/**
 * Processa erros da API e lança o tipo apropriado de erro.
 *
 * Suporta dois formatos de erro de validação:
 * 1. Array de objetos com property/constraints (formato antigo)
 * 2. Objeto com message e properties/errorMessages (formato novo)
 */
function handleApiError(error: {
  response?: {
    status: number;
    data: {
      properties?: {
        property: string;
        errorMessages: string[];
      }[];
      message?:
        | string
        | {
            property: string;
            constraints: string[];
          }[];
    };
  };
}): never {
  const status = error.response?.status;
  const data = error.response?.data;
  const errorMessage = typeof data?.message === "string" ? data.message : "";

  // Formato novo: { message: string, properties: [{ property, errorMessages }] }
  if (status === 400 && data?.properties && Array.isArray(data.properties)) {
    throw new FormError(errorMessage || "Há dados inválidos", data.properties);
  }

  // Formato antigo: Array de { property, constraints }
  if (status === 400 && Array.isArray(data?.message)) {
    const validationErrors = data.message.map((error: any) => ({
      property: error.property,
      constraints: error.constraints || {},
    }));

    throw new APIError(
      {
        message: "Erro de validação",
        validationErrors,
      },
      status,
    );
  }

  throw new APIError({ message: errorMessage || "Erro inesperado" }, status || 500);
}

export async function clientGet<T>(payload: { path: string; params?: Record<string, any> }): Promise<T> {
  try {
    const response = await api.get<T>(payload.path, { params: payload.params });
    return response.data;
  } catch (error: any) {
    handleApiError(error);
  }
}

type ClientRequestPayload = {
  path: string;
  payload?: any;
  params?: Record<string, any>;
  suppressGlobalErrorToast?: boolean;
};

function createRequestConfig(payload: ClientRequestPayload): AxiosRequestConfig {
  const config: AxiosRequestConfig = {
    params: payload.params,
    suppressGlobalErrorToast: payload.suppressGlobalErrorToast,
  };

  if (payload.payload instanceof FormData) {
    config.headers = {
      "Content-Type": "multipart/form-data",
    };
  }

  return config;
}

export async function clientPost<T>(payload: ClientRequestPayload): Promise<T> {
  try {
    const response = await api.post<T>(payload.path, payload.payload, createRequestConfig(payload));
    return response.data;
  } catch (error: any) {
    handleApiError(error);
  }
}

export async function clientPatch<T>(payload: ClientRequestPayload): Promise<T> {
  try {
    const response = await api.patch<T>(payload.path, payload.payload, createRequestConfig(payload));
    return response.data;
  } catch (error: any) {
    handleApiError(error);
  }
}

export async function clientDelete<T>(payload: { path: string; params?: Record<string, any> }): Promise<T> {
  try {
    const response = await api.delete<T>(payload.path, {
      params: payload.params,
    });
    return response.data;
  } catch (error: any) {
    handleApiError(error);
  }
}
